// src/components/modules/RocketlaneSyncLog.js
// Admin view → "Rocketlane sync": one row per run of the daily Rocketlane job; open a run to see every action
// (entries added / updated / deleted, projects and tasks created, users not matched).
// API: Controllers/RocketlaneSyncLogController.cs

import React, { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight, FiSearch } from "react-icons/fi";
import { Timesheetapi } from "../../middleware/interceptor";

const who = () => ({
  empid: localStorage.getItem("employee_id") || "",
  roleId: localStorage.getItem("emp_role_id") || "",
});
const unwrap = (res) => {
  if (res?.data?.status !== 1) throw new Error(res?.data?.message || "Request failed");
  return res.data;
};
export async function fetchSyncRuns(days = 30) {
  return unwrap(await Timesheetapi.get("api/Rocketlane/GetSyncRuns", { params: { ...who(), days } }));
}
export async function fetchSyncRunDetails(logId) {
  return unwrap(await Timesheetapi.get("api/Rocketlane/GetSyncRunDetails", { params: { ...who(), logId } }));
}

// Colours per action (text on a light chip; same family as the dashboard)
const ACTION_STYLE = {
  "Entry added": "bg-[#E7F5EE] text-[#1E6B52]",
  "Entry updated": "bg-[#E3ECFB] text-[#164AA6]",
  "Entry deleted": "bg-[#FDECEC] text-[#9F1D1D]",
  "Project created": "bg-[#F1ECFB] text-[#5B33A6]",
  "Task created": "bg-[#F1ECFB] text-[#5B33A6]",
  "User not matched": "bg-[#FFF4EC] text-[#9A3412]",
  "Entries matched": "bg-[#E7F5EE] text-[#1E6B52]",
  "Non-project placed": "bg-[#F1F3F7] text-[#3D4555]",
};
const STATUS_STYLE = {
  Success: "bg-[#E7F5EE] text-[#1E6B52]",
  DryRun: "bg-[#F1F3F7] text-[#3D4555]",
  Failed: "bg-[#FDECEC] text-[#9F1D1D]",
  Running: "bg-[#FFF4EC] text-[#9A3412]",
};

const Chip = ({ text, cls }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls || "bg-[#F1F3F7] text-[#3D4555]"}`}>{text}</span>
);

const dt = (iso) => (iso ? new Date(iso) : null);
const fmtDateTime = (iso) => {
  const d = dt(iso);
  return d ? d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
};
const fmtDate = (ymd) => (ymd ? new Date(ymd + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");
const duration = (a, b) => {
  const s = dt(a), e = dt(b);
  if (!s || !e) return "";
  const sec = Math.max(0, Math.round((e - s) / 1000));
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
};
const statusLabel = (r) => (r.status === "DryRun" || r.dry_run ? "Dry run" : r.status || "Running");

// ── One run's actions ──
// query: the search box in the Admin view toolbar (used when this run's own search box is empty)
const RunDetails = ({ logId, query = "" }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    fetchSyncRunDetails(logId)
      .then((d) => { if (!cancelled) { setData(d); setState("ready"); } })
      .catch((err) => { console.error("Sync run details failed:", err); if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [logId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const mq = (q.trim() || query.trim()).toLowerCase();
    return data.actions.filter((a) =>
      (filter === "all" || a.action === filter) &&
      (!mq || [a.employee_name, a.email, a.project, a.task].some((x) => (x || "").toLowerCase().includes(mq))));
  }, [data, filter, q, query]);

  if (state === "loading") return <div className="h-16 rounded-lg bg-[#F1F3F7] animate-pulse" />;
  if (state === "error") return <p className="m-0 text-[13px] text-[#9F1D1D]">Couldn't load this run. Try again.</p>;
  if (data.message) return <p className="m-0 text-[13px] text-[#5B6474]">{data.message}</p>;
  if (data.actions.length === 0)
    return <p className="m-0 text-[13px] text-[#5B6474]">No changes in this run{data.run.status === "Failed" ? " before it failed" : ""}.</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by action">
          <button type="button" onClick={() => setFilter("all")}
            className={`h-7 px-2.5 rounded-full border text-[12px] cursor-pointer ${filter === "all" ? "border-[#1D5FD1] bg-[#EEF3FC] text-[#164AA6] font-semibold" : "border-[#E6E9EF] bg-white text-[#3D4555]"}`}>
            All ({data.actions.length})
          </button>
          {data.summary.map((s) => (
            <button key={s.action} type="button" onClick={() => setFilter(s.action)}
              className={`h-7 px-2.5 rounded-full border text-[12px] cursor-pointer ${filter === s.action ? "border-[#1D5FD1] bg-[#EEF3FC] text-[#164AA6] font-semibold" : "border-[#E6E9EF] bg-white text-[#3D4555]"}`}>
              {s.action} ({s.count})
            </button>
          ))}
        </div>
        <label className="relative">
          <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A3]" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search person, project, task"
            aria-label="Search actions" className="h-8 w-[220px] pl-9 pr-3 rounded-[10px] border border-[#E6E9EF] text-[12px] box-border" />
        </label>
      </div>
      {data.truncated && (
        <p className="m-0 text-[12px] text-[#9A3412]">Showing the first {data.actions.length} actions of this run.</p>
      )}
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-[#EEF0F4] rounded-[10px]">
        <table className="w-full text-[12px] border-collapse min-w-[760px]">
          <thead className="sticky top-0 bg-[#F8F9FB] text-[#5B6474] text-left">
            <tr>
              <th className="font-semibold px-3 py-2">Action</th>
              <th className="font-semibold px-3 py-2">Person</th>
              <th className="font-semibold px-3 py-2">Entry date</th>
              <th className="font-semibold px-3 py-2">Project / task</th>
              <th className="font-semibold px-3 py-2 text-right">Hours</th>
              <th className="font-semibold px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a, i) => (
              <tr key={`${a.entry_id || ""}-${i}`} className="border-t border-[#F1F3F7] align-top">
                <td className="px-3 py-2"><Chip text={a.action} cls={ACTION_STYLE[a.action]} /></td>
                <td className="px-3 py-2">
                  <span className="block font-medium">{a.employee_name || a.user_name || (a.email ? "" : "—")}</span>
                  {a.email && <span className="block text-[11px] text-[#5B6474]">{a.email}</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(a.entry_date)}</td>
                <td className="px-3 py-2">
                  <span className="block">{a.project}</span>
                  {a.task && <span className="block text-[11px] text-[#5B6474]">{a.task}</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{a.hours != null ? a.hours : ""}</td>
                <td className="px-3 py-2 text-[#5B6474]">{a.details}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-3 text-[#5B6474]">No actions match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Runs list ──
// query: Admin view toolbar search — filters each opened run's actions (person, email, project, task)
export const RocketlaneSyncLog = ({ query = "" }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState(null);
  const [hideEmpty, setHideEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    fetchSyncRuns(days)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setState("ready");
        // Open the latest run that changed something
        const first = d.runs.find((r) => r.actions > 0) || d.runs[0];
        setOpen((o) => o ?? first?.id ?? null);
      })
      .catch((err) => { console.error("Sync runs failed:", err); if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [days]);

  const runs = (data?.runs || []).filter((r) => !hideEmpty || r.actions > 0 || r.status === "Failed");
  const last = data?.runs?.[0];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[13px] text-[#5B6474]">
          {last ? <>Last run {fmtDateTime(last.run_start)} · <Chip text={statusLabel(last)} cls={STATUS_STYLE[last.status] || STATUS_STYLE.Running} /></> : "No runs yet"}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-[12px] text-[#3D4555] cursor-pointer">
            <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
            Only runs with changes
          </label>
          <div role="tablist" aria-label="Range" className="flex bg-[#F1F3F7] rounded-[10px] p-1 gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} type="button" role="tab" aria-selected={days === d} onClick={() => setDays(d)}
                className={`h-8 px-3 rounded-lg border-0 text-[12px] cursor-pointer ${days === d ? "bg-white text-[#164AA6] font-semibold shadow-sm" : "bg-transparent text-[#3D4555]"}`}>
                Last {d} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {state === "loading" && (
        <div className="flex flex-col gap-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-[#F1F3F7] animate-pulse" />)}</div>
      )}
      {state === "error" && (
        <div role="alert" className="rounded-[12px] border border-[#F6C9A8] bg-[#FFF4EC] px-4 py-3 text-[13px] text-[#7C2D12]">
          Couldn't load the Rocketlane sync log. Try refreshing.
        </div>
      )}
      {state === "ready" && data && !data.detail_log && (
        <p className="m-0 text-[12px] text-[#9A3412]">
          Per-action details start after RL_Sync_Log_Detail.sql is run and the updated job is deployed. Run totals are shown below.
        </p>
      )}
      {state === "ready" && runs.length === 0 && (
        <p className="m-0 text-[13px] text-[#5B6474]">No runs in the last {days} days.</p>
      )}

      {state === "ready" && runs.length > 0 && (
        <div className="flex flex-col border border-[#EEF0F4] rounded-[12px] overflow-hidden">
          {runs.map((r) => {
            const isOpen = open === r.id;
            const changes = [
              r.inserted > 0 && `${r.inserted} added`,
              r.updated > 0 && `${r.updated} updated`,
              r.deleted > 0 && `${r.deleted} deleted`,
              r.new_projects > 0 && `${r.new_projects} new project${r.new_projects === 1 ? "" : "s"}`,
              r.new_tasks > 0 && `${r.new_tasks} new task${r.new_tasks === 1 ? "" : "s"}`,
              r.unmatched_users > 0 && `${r.unmatched_users} not matched`,
            ].filter(Boolean);
            return (
              <div key={r.id} className="border-b border-[#EEF0F4] last:border-0">
                <button type="button" onClick={() => setOpen(isOpen ? null : r.id)} aria-expanded={isOpen}
                  className="w-full grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 items-center px-4 py-3 bg-white border-0 text-left cursor-pointer hover:bg-[#F8F9FB]">
                  {isOpen ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                  <span className="flex flex-col min-w-0">
                    <span className="text-[14px] font-medium">
                      {fmtDateTime(r.run_start)} <span className="text-[12px] font-normal text-[#5B6474]">· {r.mode}{r.run_end ? ` · ${duration(r.run_start, r.run_end)}` : ""}</span>
                    </span>
                    <span className="text-[12px] text-[#5B6474] truncate">
                      {r.status === "Failed" ? r.message : `${r.fetched} fetched · ${changes.length ? changes.join(" · ") : "no changes"}`}
                    </span>
                  </span>
                  <Chip text={statusLabel(r)} cls={STATUS_STYLE[r.status] || STATUS_STYLE.Running} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 bg-white">
                    <RunDetails logId={r.id} query={query} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RocketlaneSyncLog;